// =============================================================================
// FORM BUILDER STORE (ZUSTAND)
// =============================================================================

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Form, FormField, FormSection, FormSettings, FieldType, ValidationRule } from './types';
import { FIELD_TEMPLATES } from './types';

interface FormBuilderState {
  // Form state
  form: Form | null;
  sections: FormSection[];
  settings: FormSettings;

  // UI state
  selectedFieldId: string | null;
  selectedSectionId: string | null;
  previewMode: boolean;
  isDragging: boolean;

  // Actions
  setForm: (form: Form | null) => void;
  updateFormName: (name: string) => void;
  updateFormDescription: (description: string) => void;

  // Section actions
  addSection: (afterSectionId?: string) => FormSection;
  updateSection: (sectionId: string, updates: Partial<FormSection>) => void;
  removeSection: (sectionId: string) => void;
  reorderSections: (fromIndex: number, toIndex: number) => void;

  // Field actions
  addField: (sectionId: string, fieldType: FieldType, afterFieldId?: string) => FormField;
  updateField: (sectionId: string, fieldId: string, updates: Partial<FormField>) => void;
  removeField: (sectionId: string, fieldId: string) => void;
  duplicateField: (sectionId: string, fieldId: string) => void;
  moveField: (
    fromSectionId: string,
    toSectionId: string,
    fromIndex: number,
    toIndex: number
  ) => void;

  // Validation actions
  addValidation: (sectionId: string, fieldId: string, rule: ValidationRule) => void;
  removeValidation: (sectionId: string, fieldId: string, ruleIndex: number) => void;

  // Settings actions
  updateSettings: (updates: Partial<FormSettings>) => void;

  // Selection
  selectField: (fieldId: string | null) => void;
  selectSection: (sectionId: string | null) => void;

  // Preview
  togglePreview: () => void;
  setIsDragging: (isDragging: boolean) => void;

  // Persistence
  exportForm: () => Form;
  reset: () => void;
}

const generateId = () => crypto.randomUUID();

const DEFAULT_SETTINGS: FormSettings = {
  submitLabel: 'Submit',
  successMessage: 'Thank you for your submission!',
  allowMultipleSubmissions: false,
  showProgressBar: false,
  theme: 'default',
};

export const useFormBuilderStore = create<FormBuilderState>()(
  immer((set, get) => ({
    // Initial state
    form: null,
    sections: [
      {
        id: generateId(),
        title: 'Section 1',
        fields: [],
      },
    ],
    settings: DEFAULT_SETTINGS,
    selectedFieldId: null,
    selectedSectionId: null,
    previewMode: false,
    isDragging: false,

    // Form actions
    setForm: (form) =>
      set((state) => {
        state.form = form;
        if (form) {
          state.sections = form.sections;
          state.settings = form.settings;
        } else {
          state.sections = [{ id: generateId(), title: 'Section 1', fields: [] }];
          state.settings = DEFAULT_SETTINGS;
        }
      }),

    updateFormName: (name) =>
      set((state) => {
        if (state.form) {
          state.form.name = name;
        }
      }),

    updateFormDescription: (description) =>
      set((state) => {
        if (state.form) {
          state.form.description = description;
        }
      }),

    // Section actions
    addSection: (afterSectionId) => {
      const newSection: FormSection = {
        id: generateId(),
        title: `Section ${get().sections.length + 1}`,
        fields: [],
      };

      set((state) => {
        if (afterSectionId) {
          const index = state.sections.findIndex((s) => s.id === afterSectionId);
          state.sections.splice(index + 1, 0, newSection);
        } else {
          state.sections.push(newSection);
        }
      });

      return newSection;
    },

    updateSection: (sectionId, updates) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          Object.assign(section, updates);
        }
      }),

    removeSection: (sectionId) =>
      set((state) => {
        if (state.sections.length > 1) {
          state.sections = state.sections.filter((s) => s.id !== sectionId);
          if (state.selectedSectionId === sectionId) {
            state.selectedSectionId = null;
          }
        }
      }),

    reorderSections: (fromIndex, toIndex) =>
      set((state) => {
        const item = state.sections[fromIndex];
        if (item) {
          const [removed] = state.sections.splice(fromIndex, 1);
          if (removed) state.sections.splice(toIndex, 0, removed);
        }
      }),

    // Field actions
    addField: (sectionId, fieldType, afterFieldId) => {
      const template = FIELD_TEMPLATES.find((t) => t.type === fieldType);

      const newField: FormField = {
        id: generateId(),
        type: fieldType,
        name: `field_${Date.now()}`,
        label: template?.label || 'New Field',
        validation: [],
        width: 'full',
        ...template?.defaultConfig,
      };

      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          if (afterFieldId) {
            const index = section.fields.findIndex((f) => f.id === afterFieldId);
            section.fields.splice(index + 1, 0, newField);
          } else {
            section.fields.push(newField);
          }
          state.selectedFieldId = newField.id;
        }
      });

      return newField;
    },

    updateField: (sectionId, fieldId, updates) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          const field = section.fields.find((f) => f.id === fieldId);
          if (field) {
            Object.assign(field, updates);
          }
        }
      }),

    removeField: (sectionId, fieldId) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          section.fields = section.fields.filter((f) => f.id !== fieldId);
          if (state.selectedFieldId === fieldId) {
            state.selectedFieldId = null;
          }
        }
      }),

    duplicateField: (sectionId, fieldId) => {
      const section = get().sections.find((s) => s.id === sectionId);
      const field = section?.fields.find((f) => f.id === fieldId);

      if (!field) return;

      const newField: FormField = {
        ...field,
        id: generateId(),
        name: `${field.name}_copy`,
        label: `${field.label} (copy)`,
      };

      set((state) => {
        const sec = state.sections.find((s) => s.id === sectionId);
        if (sec) {
          const index = sec.fields.findIndex((f) => f.id === fieldId);
          sec.fields.splice(index + 1, 0, newField);
          state.selectedFieldId = newField.id;
        }
      });
    },

    moveField: (fromSectionId, toSectionId, fromIndex, toIndex) =>
      set((state) => {
        const fromSection = state.sections.find((s) => s.id === fromSectionId);
        const toSection = state.sections.find((s) => s.id === toSectionId);

        if (fromSection && toSection && fromSection.fields[fromIndex]) {
          const [removed] = fromSection.fields.splice(fromIndex, 1);
          if (removed) toSection.fields.splice(toIndex, 0, removed);
        }
      }),

    // Validation actions
    addValidation: (sectionId, fieldId, rule) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          const field = section.fields.find((f) => f.id === fieldId);
          if (field) {
            field.validation.push(rule);
          }
        }
      }),

    removeValidation: (sectionId, fieldId, ruleIndex) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          const field = section.fields.find((f) => f.id === fieldId);
          if (field) {
            field.validation.splice(ruleIndex, 1);
          }
        }
      }),

    // Settings actions
    updateSettings: (updates) =>
      set((state) => {
        Object.assign(state.settings, updates);
      }),

    // Selection
    selectField: (fieldId) =>
      set((state) => {
        state.selectedFieldId = fieldId;
      }),

    selectSection: (sectionId) =>
      set((state) => {
        state.selectedSectionId = sectionId;
      }),

    // Preview
    togglePreview: () =>
      set((state) => {
        state.previewMode = !state.previewMode;
        state.selectedFieldId = null;
      }),

    setIsDragging: (isDragging) =>
      set((state) => {
        state.isDragging = isDragging;
      }),

    // Persistence
    exportForm: () => {
      const state = get();
      return {
        id: state.form?.id || generateId(),
        name: state.form?.name || 'Untitled Form',
        description: state.form?.description,
        sections: state.sections,
        settings: state.settings,
        createdAt: state.form?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },

    reset: () =>
      set((state) => {
        state.form = null;
        state.sections = [{ id: generateId(), title: 'Section 1', fields: [] }];
        state.settings = DEFAULT_SETTINGS;
        state.selectedFieldId = null;
        state.selectedSectionId = null;
        state.previewMode = false;
      }),
  }))
);
